import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Globe2, Lock, Menu, User, X } from 'lucide-react';
import { HeaderSocialLinks } from './SocialLinks';
import { getProductsByCategory, productCategories } from '@/data/products';

const legalLinks = [
  ['/privacy-policy', 'Privacy & Technical Policy'], ['/terms', 'Terms & Conditions'], ['/refund-policy', 'Refund Policy'],
  ['/customer-care', 'Customer Care'], ['/vulnerable-persons-policy', 'Vulnerable Persons'],
  ['/acceptable-use-policy', 'Acceptable Use'], ['/cookie-policy', 'Cookie Policy'], ['/adr', 'Alternative Dispute Resolution'],
];
const desktopLegalLinks = [...legalLinks, ['/faq', 'FAQ']];

const pricingAnchors = {
  vpn: 'pricing',
  travel: 'travel-packs',
  privacy: 'privacy-packs',
  unlimited: 'unlimited-packs',
  numbers: 'virtual-numbers',
};

const pricingCategoryOrder = ['vpn', 'numbers', 'privacy', 'travel', 'unlimited'];
const orderedPricingCategories = pricingCategoryOrder.map((id) => productCategories.find((category) => category.id === id));
const desktopPricingColumns = [['vpn', 'numbers', 'unlimited'], ['privacy'], ['travel']];
const getPricingDropdownLabel = (category) => category.id === 'privacy' ? 'eSIM + VPN Packs' : category.label;
const getPricingProductLabel = (product) => product.name.replace(/\s+[—–]\s+/g, ' - ');

const Header = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const headerRef = useRef(null);
  const mobilePricingHistoryRef = useRef(false);
  const mobileMenuHistoryRef = useRef(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMenus = () => { setPricingOpen(false); setLegalOpen(false); setMobileOpen(false); };

  const handlePageNavigation = (event, destination) => {
    event.preventDefault();
    const hasTemporaryMenuEntry = mobileMenuHistoryRef.current || mobilePricingHistoryRef.current;
    closeMenus();

    const openDestination = () => {
      if (pathname === '/') {
        // Make Back return to the clean homepage, never the previous pricing/product anchor.
        window.history.replaceState({ ...window.history.state }, '', '/');
        navigate(destination);
      } else {
        // Moving between information pages should not build a chain of pages.
        navigate(destination, { replace: true });
      }
    };

    if (hasTemporaryMenuEntry) {
      mobileMenuHistoryRef.current = false;
      mobilePricingHistoryRef.current = false;
      window.addEventListener('popstate', () => {
        window.requestAnimationFrame(openDestination);
      }, { once: true });
      window.history.back();
      return;
    }

    openDestination();
  };

  const toggleMobilePricing = () => {
    setMobileOpen(false);
    setLegalOpen(false);

    if (pricingOpen) {
      setPricingOpen(false);
      if (mobilePricingHistoryRef.current) {
        mobilePricingHistoryRef.current = false;
        window.history.back();
      }
      return;
    }

    if (mobileMenuHistoryRef.current) {
      window.history.replaceState({ ...window.history.state, phantomPathMobileMenu: false, phantomPathPricingMenu: true }, '', window.location.href);
      mobileMenuHistoryRef.current = false;
      mobilePricingHistoryRef.current = true;
      setPricingOpen(true);
      return;
    }

    window.history.pushState({ ...window.history.state, phantomPathPricingMenu: true }, '', window.location.href);
    mobilePricingHistoryRef.current = true;
    setPricingOpen(true);
  };

  const toggleMobileMenu = () => {
    setPricingOpen(false);
    setLegalOpen(false);

    if (mobileOpen) {
      setMobileOpen(false);
      if (mobileMenuHistoryRef.current) {
        mobileMenuHistoryRef.current = false;
        window.history.back();
      }
      return;
    }

    if (mobilePricingHistoryRef.current) {
      window.history.replaceState({ ...window.history.state, phantomPathPricingMenu: false, phantomPathMobileMenu: true }, '', window.location.href);
      mobilePricingHistoryRef.current = false;
      mobileMenuHistoryRef.current = true;
      setMobileOpen(true);
      return;
    }

    window.history.pushState({ ...window.history.state, phantomPathMobileMenu: true }, '', window.location.href);
    mobileMenuHistoryRef.current = true;
    setMobileOpen(true);
  };

  const handlePricingProductClick = (productSlug) => {
    closeMenus();
    let attempts = 0;
    const scrollToProduct = () => {
      const productCard = document.getElementById(productSlug);
      if (productCard) {
        productCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < 12) window.setTimeout(scrollToProduct, 50);
    };
    window.setTimeout(scrollToProduct, 0);
  };

  useEffect(() => {
    const closeMobileMenusOnBack = () => {
      if (!mobilePricingHistoryRef.current && !mobileMenuHistoryRef.current) return;
      mobilePricingHistoryRef.current = false;
      mobileMenuHistoryRef.current = false;
      setPricingOpen(false);
      setMobileOpen(false);
    };

    window.addEventListener('popstate', closeMobileMenusOnBack);
    return () => window.removeEventListener('popstate', closeMobileMenusOnBack);
  }, []);

  useEffect(() => {
    if (!pricingOpen && !legalOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setPricingOpen(false);
        setLegalOpen(false);
      }
    };

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setPricingOpen(false);
        setLegalOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [pricingOpen, legalOpen]);

  return (
    <header ref={headerRef} className="fixed inset-x-0 top-0 z-50 h-[90px] border-b border-white/5 bg-[#050b14] shadow-sm">
      <div className="flex h-[90px] w-full items-center px-4 md:px-4 xl:px-6 min-[1600px]:px-12">
        <a href="/" onClick={(event) => { event.preventDefault(); closeMenus(); window.location.assign('/'); }} className="flex shrink-0 items-center gap-2" aria-label="PhantomPathVPN home">
          <span className="header-logo-mark h-12 w-12 xl:h-16 xl:w-16" aria-hidden="true" />
          <span className="hidden text-lg font-extrabold tracking-[0.015em] text-white min-[1600px]:block min-[1600px]:text-[24px]">PHANTOM<span className="text-[#078df0]">PATH</span></span>
        </a>

        <nav className="ml-auto hidden items-center justify-end gap-3 md:flex lg:gap-5 xl:gap-5 min-[1600px]:gap-7" aria-label="Primary navigation">
          <Link to="/zacks-manifesto" onClick={(event) => handlePageNavigation(event, '/zacks-manifesto')} className="ml-2 flex items-center gap-1.5 whitespace-nowrap text-[13px] font-bold uppercase tracking-[0.015em] text-gray-200 transition hover:text-[#00ffc8] lg:text-[15px] xl:ml-0 xl:text-base min-[1600px]:ml-3">
            <User className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Meet the Founder</span>
          </Link>
          <Link to="/zero-log-manifesto" onClick={(event) => handlePageNavigation(event, '/zero-log-manifesto')} className="group flex -translate-y-[7px] flex-col items-center whitespace-nowrap px-1.5 leading-none transition">
            <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-gray-500 transition group-hover:text-[#00ffc8]/70 lg:text-[10px]">PhantomPath</span>
            <span className="mt-1 text-[13px] font-bold uppercase tracking-[0.025em] text-gray-200 transition group-hover:text-[#00ffc8] lg:text-[15px] xl:text-base">Technical Manifesto</span>
          </Link>
          <Link to="/how-it-works" onClick={(event) => handlePageNavigation(event, '/how-it-works')} className="whitespace-nowrap text-[13px] font-bold uppercase tracking-[0.015em] text-gray-200 transition hover:text-[#00ffc8] lg:text-[15px] xl:text-base">How It Works</Link>

          <div className="relative">
            <button onClick={() => { setLegalOpen(false); setPricingOpen((open) => !open); }} aria-expanded={pricingOpen} className="flex items-center gap-1 whitespace-nowrap py-3 text-[13px] font-bold uppercase tracking-[0.015em] text-gray-200 transition hover:text-[#00ffc8] lg:text-[15px] xl:text-base">
              Pricing <ChevronDown className="h-3 w-3 xl:h-4 xl:w-4" />
            </button>

            <div className={`fixed left-1/2 top-[90px] max-h-[68vh] w-[88vw] max-w-[1180px] -translate-x-1/2 overflow-y-auto rounded-b-2xl border border-white/10 bg-[#0b1322]/[0.98] shadow-2xl backdrop-blur-xl transition duration-200 ${pricingOpen ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-2 opacity-0'}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-5 py-4 lg:px-7">
                <div>
                  <p className="text-base font-bold text-white lg:text-lg">Choose a service</p>
                  <div className="mt-2 flex max-w-3xl items-start gap-2 text-[11px] leading-relaxed text-[#173c5e] lg:text-xs">
                    <Globe2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#087da9]" aria-hidden="true" />
                    <div className="text-[99%]">
                      <p>International customers welcome. Prices are shown in GBP (£). Card, PayPal, Apple Pay, Google Pay and other supported payment methods are available. Currency conversion is handled automatically by your payment provider.</p>
                    </div>
                  </div>
                  <p className="mt-2 max-w-3xl text-[10px] leading-relaxed text-gray-400 lg:text-[11px]">PhantomPath retains only the limited order information required for provisioning, accounting, refunds, fraud prevention and support. This information is never linked to VPN traffic, browsing activity, session content or communications content.</p>
                </div>
              </div>

              <div className="grid gap-x-8 p-5 md:grid-cols-3 lg:p-7">
                {desktopPricingColumns.map((categoryIds, columnIndex) => (
                  <div key={columnIndex} className="space-y-6">
                    {categoryIds.map((id) => orderedPricingCategories.find((category) => category.id === id)).map((category) => (
                      <section key={category.id}>
                        <h2 className="public-pricing-category-label mb-3 text-xs font-black uppercase tracking-[0.14em] lg:text-sm" style={{ color: category.accent }}>{getPricingDropdownLabel(category)}</h2>
                        <ul className="space-y-1">
                          {getProductsByCategory(category.id).map((product) => (
                            <li key={product.slug}>
                              <Link to={`/#${product.slug}`} onClick={() => handlePricingProductClick(product.slug)} className="public-dropdown-link flex items-center justify-between gap-3 rounded-lg px-2 py-2 text-xs font-semibold text-gray-200 transition hover:bg-white/5 hover:text-white lg:text-sm">
                                <span>{getPricingProductLabel(product)}</span>
                                <span className="shrink-0 font-bold text-white">{product.price}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </section>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="relative">
            <button onClick={() => { setPricingOpen(false); setLegalOpen((open) => !open); }} aria-expanded={legalOpen} className="flex items-center gap-1 whitespace-nowrap py-3 text-[13px] font-bold uppercase tracking-[0.015em] text-gray-200 transition hover:text-[#00ffc8] lg:text-[15px] xl:text-base">Legal &amp; Privacy <ChevronDown className="h-3 w-3 xl:h-4 xl:w-4" /></button>
            <div className={`absolute right-0 top-full w-72 overflow-hidden rounded-2xl border border-white/10 bg-[#0f172a]/[0.98] py-2 shadow-2xl backdrop-blur-xl transition duration-200 ${legalOpen ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-2 opacity-0'}`}>
              <div className="public-legal-heading border-b border-white/10 px-6 py-3 text-xs font-black uppercase tracking-[0.14em] text-[#00ffc8]">Legal pages</div>
              <div className="divide-y divide-white/5">{desktopLegalLinks.map(([href, label]) => <Link key={href} to={href} onClick={(event) => handlePageNavigation(event, href)} className="public-dropdown-link block px-6 py-3 text-[15px] font-semibold text-gray-200 transition hover:bg-white/5 hover:text-[#00ffc8]">{label}</Link>)}</div>
            </div>
          </div>

          <div className="hidden border-l border-white/10 pl-7 xl:block"><HeaderSocialLinks /></div>
          <a href="/#service-options" className="header-services-cta mr-7 hidden h-[46px] shrink-0 items-center rounded-lg px-6 text-[14.5px] font-bold transition md:flex lg:mr-8 xl:mr-10 xl:h-[48px] xl:px-7 xl:text-[15px]">View Services</a>
        </nav>

        <div className="mobile-header-actions ml-auto flex items-center gap-2 md:hidden">
          <a
            href="https://portal.phantompathvpn.com/"
            className="mobile-header-portal flex items-center gap-1 rounded-lg px-2 py-2.5 text-xs font-bold uppercase tracking-[0.015em] text-white transition hover:text-[#00ffc8]"
          >
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            Portal
          </a>
          <button
            onClick={toggleMobilePricing}
            className="mobile-header-pricing flex items-center gap-1 rounded-lg px-2 py-2.5 text-xs font-bold uppercase tracking-[0.015em] text-white transition hover:text-[#00ffc8]"
            aria-label="Toggle pricing menu"
            aria-expanded={pricingOpen}
          >
            Pricing <ChevronDown className={`h-3.5 w-3.5 transition-transform ${pricingOpen ? 'rotate-180' : ''}`} />
          </button>
          <button onClick={toggleMobileMenu} className="mobile-header-menu rounded-lg border border-white/10 p-2.5 text-white" aria-label="Toggle menu" aria-expanded={mobileOpen}>{mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
        </div>
      </div>

      {pricingOpen && (
        <nav className="max-h-[calc(100vh-90px)] overflow-y-auto border-t border-white/10 bg-[#07101d] px-4 py-5 shadow-2xl md:hidden" aria-label="Mobile pricing navigation">
          <div className="mx-auto max-w-xl">
            <div className="mb-4 border-b border-white/10 pb-4">
              <div>
                <div>
                  <p className="font-bold text-white">Choose a service</p>
                </div>
              </div>
              <div className="mt-2 flex items-start gap-2 text-[11px] leading-relaxed text-[#173c5e]">
                <Globe2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#087da9]" aria-hidden="true" />
                <div className="text-[99%]">
                  <p>International customers welcome. Prices are shown in GBP (£). Card, PayPal, Apple Pay, Google Pay and other supported payment methods are available. Currency conversion is handled automatically by your payment provider.</p>
                </div>
              </div>
              <p className="mt-2 text-[10px] leading-relaxed text-gray-400">PhantomPath retains only the limited order information required for provisioning, accounting, refunds, fraud prevention and support. This information is never linked to VPN traffic, browsing activity, session content or communications content.</p>
            </div>
            <div className="space-y-5">
              {orderedPricingCategories.map((category) => (
                <section key={category.id}>
                  <Link to={`/#${pricingAnchors[category.id]}`} onClick={closeMenus} className="public-pricing-category-label block text-sm font-black uppercase tracking-[0.1em]" style={{ color: category.accent }}>{getPricingDropdownLabel(category)}</Link>
                  <div className="mt-2 space-y-1">
                    {getProductsByCategory(category.id).map((product) => (
                      <Link key={product.slug} to={`/#${product.slug}`} onClick={() => handlePricingProductClick(product.slug)} className="public-dropdown-link flex items-center justify-between gap-3 rounded-lg px-2 py-2 text-xs text-gray-300 hover:bg-white/5">
                        <span>{getPricingProductLabel(product)}</span><span className="shrink-0 font-bold text-white">{product.price}</span>
                      </Link>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </nav>
      )}

      {mobileOpen && (
        <nav className="max-h-[calc(100vh-90px)] overflow-y-auto border-t border-[#00ffc8]/15 bg-gradient-to-b from-[#05080d]/[0.99] to-black/[0.99] px-4 py-5 shadow-2xl backdrop-blur-xl md:hidden" aria-label="Mobile navigation">
          <div className="mx-auto max-w-xl space-y-2">
            <a href="/#service-options" onClick={closeMenus} className="header-services-cta flex items-center justify-center rounded-xl px-4 py-3 text-sm font-bold transition">
              View Services
            </a>
            <Link to="/zacks-manifesto" onClick={(event) => handlePageNavigation(event, '/zacks-manifesto')} className="flex items-center gap-2 rounded-lg px-3 py-3 font-semibold text-gray-200 hover:bg-white/5">
              <User className="h-4 w-4 shrink-0 text-[#123b60]" aria-hidden="true" />
              <span>Meet the Founder</span>
            </Link>
            <Link to="/about-us" onClick={(event) => handlePageNavigation(event, '/about-us')} className="block rounded-lg py-3 pl-9 pr-3 font-semibold text-gray-200 hover:bg-white/5">About Us</Link>
            <Link to="/how-it-works" onClick={(event) => handlePageNavigation(event, '/how-it-works')} className="block rounded-lg py-3 pl-9 pr-3 font-semibold text-gray-200 hover:bg-white/5">How It Works</Link>
            <Link to="/zero-log-manifesto" onClick={(event) => handlePageNavigation(event, '/zero-log-manifesto')} className="block rounded-lg py-3 pl-9 pr-3 font-semibold text-gray-200 hover:bg-white/5">Technical Manifesto</Link>
            <details className="rounded-lg border border-white/10 bg-white/[0.02] p-3"><summary className="public-legal-heading cursor-pointer font-semibold text-gray-200">Legal &amp; Privacy</summary><div className="mt-2">{legalLinks.map(([href, label]) => <Link key={href} to={href} onClick={(event) => handlePageNavigation(event, href)} className="public-dropdown-link block py-2 text-sm text-gray-400">{label}</Link>)}</div></details>
            <Link to="/faq" onClick={(event) => handlePageNavigation(event, '/faq')} className="block rounded-lg py-3 pl-9 pr-3 font-semibold text-gray-200 hover:bg-white/5">FAQ</Link>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
